import { MasterApprenticesRepublicResponse, RepublicTableRow } from '../models';

const HEADER_BG = 'FFF3F4F6';
const TOTAL_BG = 'FFF3F4F6';
const ROW_ODD_BG = 'FFF9FAFB';
const ROW_EVEN_BG = 'FFFFFFFF';
const BORDER_COLOR = 'FFD1D5DB';
const DYNAMIC_HEADER_BG = 'FFF3F4F6';

const border = () => ({
  top: { style: 'thin' as const, color: { argb: BORDER_COLOR } },
  left: { style: 'thin' as const, color: { argb: BORDER_COLOR } },
  bottom: { style: 'thin' as const, color: { argb: BORDER_COLOR } },
  right: { style: 'thin' as const, color: { argb: BORDER_COLOR } },
});

interface ExcelColumn {
  key: string;
  label: string;
  isDynamic: boolean;
  actionId?: string;
}

function buildColumns(
  response: MasterApprenticesRepublicResponse,
): ExcelColumn[] {
  const CONVERTED_LABEL: Record<string, string> = {
    region_name: 'Hudud nomi',
    district_name: 'Tuman nomi',
    master_name: 'Ustozlar',
    masters_count: 'Ustoz f/x lar soni',
    apprentices_count: 'Shogird f/x lar soni',
    contours_count: 'Konturlar soni',
  };

  const staticCols: ExcelColumn[] = response.columns.static.map((col) => ({
    key: col.key,
    label: CONVERTED_LABEL[col.key] ?? col.ui_label,
    isDynamic: false,
  }));

  const dynamicCols: ExcelColumn[] = response.columns.dynamic.actions.map(
    (action) => ({
      key: `action_${action.action_id}`,
      label: action.action_name,
      isDynamic: true,
      actionId: action.action_id,
    }),
  );

  return [...staticCols, ...dynamicCols];
}

function resolveCellValue(
  col: ExcelColumn,
  row: RepublicTableRow,
): string | number {
  if (col.isDynamic && col.actionId) {
    const match = row.rated_actions.find((ra) => ra.action_id === col.actionId);
    return match?.rated_actions_count ?? 0;
  }
  const value = row[col.key as keyof RepublicTableRow];
  if (typeof value === 'number') return value;
  return String(value ?? '');
}

function buildTotals(
  columns: ExcelColumn[],
  rows: RepublicTableRow[],
  response: MasterApprenticesRepublicResponse,
): Record<string, number> {
  const totals: Record<string, number> = {};

  const aggregatableStaticKeys = response.columns.static
    .filter((col) => col.aggregate === 'total')
    .map((col) => col.key);

  rows.forEach((row) => {
    aggregatableStaticKeys.forEach((key) => {
      const value = row[key as keyof RepublicTableRow];
      totals[key] =
        (totals[key] ?? 0) + (typeof value === 'number' ? value : 0);
    });

    row.rated_actions.forEach((ra) => {
      const key = `action_${ra.action_id}`;
      totals[key] = (totals[key] ?? 0) + ra.rated_actions_count;
    });
  });

  return totals;
}

export async function exportTeacherStudentRepublicToExcel(
  response: MasterApprenticesRepublicResponse,
  filename = 'ustoz-shogird-respublika',
): Promise<void> {
  const ExcelJS = (await import('exceljs')).default;
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('Ustoz-shogird respublika');

  const columns = buildColumns(response);
  const rows = response.rows;
  const totals = buildTotals(columns, rows, response);
  const groupLabel = response.columns.dynamic.group_value;

  const staticCount = response.columns.static.length;
  const dynamicCount = response.columns.dynamic.actions.length;

  ws.columns = [
    { width: 6 },
    ...columns.map((col) =>
      col.key === 'region_name' ? { width: 30 } : { width: 22 },
    ),
  ];

  // ─── HEADER ROW 1: # + static (rowSpan=2 via merge) + dynamic group ──────
  const headerRow1Values = ['#', ...columns.map(() => '')];
  const hRow1 = ws.addRow(headerRow1Values);
  hRow1.height = 36;

  // Static ustunlarni vertikal merge qilish uchun belgilash
  const staticEndCol = 1 + staticCount; // 1-indexed, # column + static cols

  // Dynamic group header
  if (dynamicCount > 0) {
    const dynStartCol = staticEndCol + 1;
    const dynEndCol = staticEndCol + dynamicCount;
    ws.mergeCells(1, dynStartCol, 1, dynEndCol);
    const groupCell = ws.getCell(1, dynStartCol);
    groupCell.value = groupLabel;
    groupCell.font = { bold: true, size: 10 };
    groupCell.alignment = { horizontal: 'center', vertical: 'middle' };
    groupCell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: DYNAMIC_HEADER_BG },
    };
    groupCell.border = border();
  }

  // ─── HEADER ROW 2: column labels ─────────────────────────────────────────
  const headerRow2Values = ['#', ...columns.map((col) => col.label)];
  const hRow2 = ws.addRow(headerRow2Values);
  hRow2.height = 44;
  hRow2.eachCell({ includeEmpty: true }, (cell, colNumber) => {
    cell.font = { bold: true, size: 10 };
    cell.alignment = {
      horizontal: 'center',
      vertical: 'middle',
      wrapText: true,
    };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: {
        argb: colNumber > staticEndCol ? DYNAMIC_HEADER_BG : HEADER_BG,
      },
    };
    cell.border = border();
  });

  // Static ustunlarni 1-2 qatorlar bo'yicha merge
  for (let c = 1; c <= staticEndCol; c++) {
    ws.mergeCells(1, c, 2, c);
    const cell = ws.getCell(1, c);
    cell.value = c === 1 ? '№' : (columns[c - 2]?.label ?? '');
    cell.font = { bold: true, size: 10 };
    cell.alignment = {
      horizontal: 'center',
      vertical: 'middle',
      wrapText: true,
    };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: HEADER_BG },
    };
    cell.border = border();
  }

  // ─── TOTALS ROW ──────────────────────────────────────────────────────────
  const totalsValues: (string | number)[] = [
    '',
    ...columns.map((col) => {
      if (col.key === 'region_name') return 'Jami:';
      return totals[col.key] ?? 0;
    }),
  ];
  const totalRow = ws.addRow(totalsValues);
  totalRow.height = 24;
  totalRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: TOTAL_BG },
    };
    cell.border = {
      ...border(),
      top: { style: 'medium', color: { argb: BORDER_COLOR } },
    };
    cell.font = { bold: true, size: 10 };
    cell.alignment = {
      horizontal: colNumber <= 2 ? 'left' : 'center',
      vertical: 'middle',
    };
  });

  // ─── DATA ROWS ───────────────────────────────────────────────────────────
  rows.forEach((row, idx) => {
    const rowBg = idx % 2 === 0 ? ROW_EVEN_BG : ROW_ODD_BG;
    const values: (string | number)[] = [
      idx + 1,
      ...columns.map((col) => resolveCellValue(col, row)),
    ];
    const excelRow = ws.addRow(values);
    excelRow.height = 22;
    excelRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: rowBg },
      };
      cell.border = border();
      if (colNumber === 1) {
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.font = { size: 10, color: { argb: 'FF6B7280' } };
      } else if (colNumber === 2) {
        cell.alignment = { horizontal: 'left', vertical: 'middle' };
        cell.font = { bold: true, size: 10 };
      } else {
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.font = { size: 10, color: { argb: 'FF1E40AF' } };
      }
    });
  });

  // ─── FREEZE PANES ────────────────────────────────────────────────────────
  ws.views = [{ state: 'frozen', xSplit: 2, ySplit: 3 }];

  // ─── DOWNLOAD ────────────────────────────────────────────────────────────
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}
